import type { EobItem, PractitionerItem } from '@/services/api/fhirAPI/explanationOfBenefit/eob'
import { ref } from 'vue'

const storeNames = {
	db: 'BlueprintClaims',
	claims: 'ClaimsStore',
	practitioners: 'PractitionerStore'
}

const version = ref(1)

export function claimsDbInitialize(): Promise<void> {
	return new Promise((resolve, reject) => {
		// First, open without a version to detect the current one
		const detectRequest = window.indexedDB.open(storeNames.db)

		detectRequest.onsuccess = (event) => {
			const db = (event.target as IDBOpenDBRequest).result
			const currentVersion = db.version
			const storesMissing =
				!db.objectStoreNames.contains(storeNames.claims) ||
				!db.objectStoreNames.contains(storeNames.practitioners)

			db.close()

			// Only bump version if stores are actually missing
			const targetVersion = storesMissing ? currentVersion + 1 : currentVersion
			version.value = targetVersion

			const upgradeRequest = window.indexedDB.open(storeNames.db, targetVersion)

			upgradeRequest.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result

				if (!db.objectStoreNames.contains(storeNames.claims)) {
					db.createObjectStore(storeNames.claims, { keyPath: 'resource.id' })
				}
				if (!db.objectStoreNames.contains(storeNames.practitioners)) {
					db.createObjectStore(storeNames.practitioners, { keyPath: 'resource.id' })
				}
			}

			upgradeRequest.onsuccess = (event) => {
				version.value = (event.target as IDBOpenDBRequest).result.version
				resolve()
			}

			upgradeRequest.onerror = (event) => reject(event)
		}

		detectRequest.onerror = (event) => reject(event)
	})
}

export function claimsDbUpdate(
	claims: EobItem[],
	practitioners: PractitionerItem[]
): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = window.indexedDB.open(storeNames.db, version.value)
		let db: IDBOpenDBRequest['result']

		request.onerror = (event) => {
			console.error(`claimsDbUpdate - failed to open`, event)
			reject(event)
		}

		request.onsuccess = (event) => {
			db = (event.target as IDBOpenDBRequest).result

			// Guard: ensure stores exist before attempting transaction
			if (
				!db.objectStoreNames.contains(storeNames.claims) ||
				!db.objectStoreNames.contains(storeNames.practitioners)
			) {
				console.warn('claimsDbUpdate - object stores not found, skipping update')
				resolve()
				return
			}

			// Use a single transaction spanning both stores
			const tx = db.transaction([storeNames.claims, storeNames.practitioners], 'readwrite')

			const claimsStore = tx.objectStore(storeNames.claims)
			claimsStore.clear()
			claims.forEach((x) => claimsStore.put(x))

			const practitionersStore = tx.objectStore(storeNames.practitioners)
			practitionersStore.clear()
			practitioners.forEach((x) => practitionersStore.put(x))

			tx.oncomplete = () => resolve()
			tx.onerror = (e) => reject(e)
		}
	})
}

interface AllClaimsData {
	claims: EobItem[]
	practitioners: PractitionerItem[]
}

export function claimsDbGetAll(): Promise<AllClaimsData> {
	return new Promise((resolve, reject) => {
		const request = window.indexedDB.open(storeNames.db, version.value)
		let db: IDBOpenDBRequest['result']

		const results: AllClaimsData = {
			claims: [],
			practitioners: []
		}

		request.onerror = (event) => {
			console.error(`claimsDbGetAll - ${storeNames.db} does not seem to exist`, event)
		}

		request.onsuccess = (event) => {
			// Save the IDBDatabase interface
			db = (event.target as IDBOpenDBRequest).result

			if (
				db.objectStoreNames.contains(storeNames.claims) &&
				db.objectStoreNames.contains(storeNames.practitioners)
			) {
				// ====---------------====
				// Setup and store claims
				// ====---------------====
				const claimsTx = db.transaction(storeNames.claims, 'readwrite')
				const claimsStore = claimsTx.objectStore(storeNames.claims)

				// ====---------------====
				// Setup and store Providers
				// ====---------------====
				const practitionersTx = db.transaction(storeNames.practitioners, 'readwrite')
				const practitionersStore = practitionersTx.objectStore(storeNames.practitioners)

				// Fetch claims data
				claimsStore.getAll().onsuccess = (event) => {
					const dbContent = (event.target as IDBRequest).result as EobItem[]
					dbContent.forEach((x) => results.claims.push(x))
				}
				practitionersStore.getAll().onsuccess = (event) => {
					const dbContent = (event.target as IDBRequest).result as PractitionerItem[]
					dbContent.forEach((x) => results.practitioners.push(x))
				}

				practitionersTx.oncomplete = (e) => resolve(results)
			} else {
				resolve(results)
			}
		}
	})
}

export function claimsDbClear(): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = window.indexedDB.open(storeNames.db)
		let db: IDBOpenDBRequest['result']

		request.onerror = (event) => {
			// Do something with request.error!
			console.error(`claimsDbClear - ${storeNames.db} does not seem to exist`)
			reject()
		}

		request.onsuccess = (event) => {
			db = (event.target as IDBOpenDBRequest).result

			// ====---------------====
			// Setup and store claims
			// ====---------------====
			if (db.objectStoreNames.contains(storeNames.claims)) {
				const claimsTx = db.transaction(storeNames.claims, 'readwrite')
				const claimsStore = claimsTx.objectStore(storeNames.claims)
				claimsStore.clear()
			}

			// ====---------------====
			// Setup and store Providers
			// ====---------------====
			if (db.objectStoreNames.contains(storeNames.practitioners)) {
				const practitionersTx = db.transaction(storeNames.practitioners, 'readwrite')
				const practitionersStore = practitionersTx.objectStore(storeNames.practitioners)
				practitionersStore.clear()
				practitionersTx.oncomplete = (e) => resolve()
			}

			db.close()
		}
	})
}
